import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Countries } from '../../../../constants';
import { FetchStatus, ModalName } from '../../../../enums';
import { useCloseModal } from '../../../../features/modals';
import {
  useCreateOrganizationUser,
  useOrganization,
  useResetCreateOrganizationUserStatus,
} from '../../../../features/organization';
import { ICreateOrganizationUser } from '../../../../interfaces';
import { areValuesValid } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';

export const CreateUserModal = () => {
  const { t } = useTranslation();
  const {
    status: { createUser: status },
  } = useOrganization();
  const createOrganizationUser = useCreateOrganizationUser();
  const resetCreateOrganizationUserStatus = useResetCreateOrganizationUserStatus();
  const [user, setUser] = useState<ICreateOrganizationUser>({
    country: '',
    firstName: '',
    lastName: '',
    telephone: '',
    username: '',
  });
  const closeModal = useCloseModal(ModalName.CREATE_USER);

  const handleChange = (key: string) => (value: string) => {
    setUser({
      ...user,
      [key]: value,
    });
  };

  const isValid = useMemo(
    () =>
      areValuesValid([
        user.country,
        user.firstName,
        user.lastName,
        { minLength: 8, value: user.telephone },
        { type: 'email', value: user.username },
      ]),
    [user],
  );

  const create = useCallback(() => {
    createOrganizationUser(user);
  }, [createOrganizationUser, user]);

  useEffect(() => {
    if (status === FetchStatus.SUCCESS) {
      closeModal();
      resetCreateOrganizationUserStatus();
    }
  }, [status]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button
          text={t('modal.createUser.button.create')}
          disabled={!isValid}
          loading={status === FetchStatus.LOADING}
          onClick={create}
        />
        <Button text={t('modal.createUser.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, create, isValid, status]);

  return (
    <Modal name={ModalName.CREATE_USER} header={t('modal.createUser.title')} footer={footer}>
      <p className="typography-paragraph-3 text-text-secondary mb-6">{t('modal.createUser.description')}</p>

      <FormFields>
        <Input
          value={user.country}
          label={t('modal.createUser.field.country.label')}
          onChange={handleChange('country')}
          options={Countries.reduce((acc, country) => ({ ...acc, [country]: t(`common.country.${country}`) }), {})}
          placeholder={t('modal.createUser.field.country.placeholder')}
          type="select"
        />

        <Input
          value={user.firstName}
          label={t('modal.createUser.field.firstName.label')}
          onChange={handleChange('firstName')}
          placeholder={t('modal.createUser.field.firstName.placeholder')}
        />

        <Input
          value={user.lastName}
          label={t('modal.createUser.field.lastName.label')}
          onChange={handleChange('lastName')}
          placeholder={t('modal.createUser.field.lastName.placeholder')}
        />

        <Input
          value={user.telephone}
          label={t('modal.createUser.field.telephone.label')}
          onChange={handleChange('telephone')}
          placeholder={t('modal.createUser.field.telephone.placeholder')}
        />

        <Input
          value={user.username}
          label={t('modal.createUser.field.username.label')}
          onChange={handleChange('username')}
          placeholder={t('modal.createUser.field.username.placeholder')}
          type="email"
        />
      </FormFields>
    </Modal>
  );
};
